import { useCallback, useEffect, useRef, useState } from 'react';
import { audioBus } from '@/lib/audioBus';

export interface PlayableTrack {
  id: string;
  title: string;
  audioUrl: string;
  duration: number;
  initialPosition: number;
}

export interface PlayerState {
  activeId: string;
  playing: boolean;
  loading: boolean;
  positions: Record<string, number>;
  durations: Record<string, number>;
}

const initState = (tracks: PlayableTrack[]): PlayerState => ({
  activeId: tracks[0]?.id ?? '',
  playing: false,
  loading: false,
  positions: Object.fromEntries(tracks.map((t) => [t.id, t.initialPosition])),
  durations: Object.fromEntries(tracks.map((t) => [t.id, t.duration])),
});

/**
 * Real HTML5 audio playback. Tracks without an `audioUrl` are never "played":
 * `toggle` returns false so the UI can show a notice instead of faking playback.
 */
export function useAudioPlayer(tracks: PlayableTrack[], owner = 'featured') {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeRef = useRef<string>('');
  const [state, setState] = useState<PlayerState>(() => initState(tracks));
  const key = tracks.map((t) => `${t.id}:${t.audioUrl}:${t.duration}`).join('|');

  // reset when the track list changes (content loaded / edited)
  useEffect(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.removeAttribute('src');
    const s = initState(tracks);
    activeRef.current = s.activeId;
    setState(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;
    const onTime = () =>
      setState((s) => ({ ...s, positions: { ...s.positions, [activeRef.current]: audio.currentTime } }));
    const onMeta = () =>
      setState((s) =>
        isFinite(audio.duration) && audio.duration > 0
          ? { ...s, durations: { ...s.durations, [activeRef.current]: audio.duration } }
          : s,
      );
    const onPlaying = () => setState((s) => ({ ...s, playing: true, loading: false }));
    const onWaiting = () => setState((s) => ({ ...s, loading: true }));
    const onPause = () => setState((s) => ({ ...s, playing: false, loading: false }));
    const onEnd = () =>
      setState((s) => ({ ...s, playing: false, loading: false, positions: { ...s.positions, [activeRef.current]: 0 } }));
    const onErr = () => setState((s) => ({ ...s, playing: false, loading: false }));
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('error', onErr);
    const offBus = audioBus.on((o) => o !== owner && audio.pause());
    return () => {
      offBus();
      audio.pause();
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('error', onErr);
    };
  }, [owner]);

  const toggle = useCallback(
    (track: PlayableTrack): boolean => {
      const audio = audioRef.current;
      if (!audio) return false;
      if (!track.audioUrl) {
        if (activeRef.current !== track.id) audio.pause();
        activeRef.current = track.id;
        setState((s) => ({ ...s, activeId: track.id, playing: false }));
        return false;
      }
      const same = activeRef.current === track.id && audio.getAttribute('src') === track.audioUrl;
      if (same && !audio.paused) {
        audio.pause();
        return true;
      }
      if (!same) {
        audio.pause();
        audio.src = track.audioUrl;
        const start = state.positions[track.id] || 0;
        if (start > 0) {
          const seekOnce = () => {
            audio.currentTime = start;
            audio.removeEventListener('loadedmetadata', seekOnce);
          };
          audio.addEventListener('loadedmetadata', seekOnce);
        }
      }
      activeRef.current = track.id;
      setState((s) => ({ ...s, activeId: track.id, loading: true }));
      audioBus.claim(owner);
      audio.play().catch(() => setState((s) => ({ ...s, playing: false, loading: false })));
      return true;
    },
    [state.positions, owner],
  );

  const seek = useCallback((track: PlayableTrack, fraction: number) => {
    const audio = audioRef.current;
    if (!audio || !track.audioUrl || activeRef.current !== track.id || !audio.getAttribute('src')) return false;
    const d = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : track.duration;
    audio.currentTime = Math.max(0, Math.min(d, d * fraction));
    setState((s) => ({ ...s, positions: { ...s.positions, [track.id]: audio.currentTime } }));
    return true;
  }, []);

  return { state, toggle, seek };
}

export const fmtTime = (s: number) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
